"use client";

import { ActiveLink } from "@/components/navigation/active-link";
import { useLocation } from 'react-router';
import { cn } from "../../lib/utils";
import {
  ChevronDown,
  LayoutDashboard,
  Gamepad2,
  Swords,
  DollarSign,
  QrCode,
  Settings,
  Users,
  MonitorPlay,
  Trophy,
  Building2,
  Target,
  Package,
  ShoppingCart,
  ShoppingBag,
  CreditCard,
  MessageSquare,
  Plug,
  Crosshair,
  Shield,
  ListOrdered,
  Activity,
  Blocks,
  Clapperboard,
} from "lucide-react";
import { useState } from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { useOrganization } from "../../lib/OrganizationContext";
import { usePermissions } from "../../hooks/usePermissions";
import { useRolePermissions } from "../../hooks/useRolePermissions";
import { Loader } from "@/components/ui/loader";
import type {
  NavigationPermission,
  NavigationCategoryWithItems,
} from "@/types/navigation";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  Gamepad2,
  Swords,
  DollarSign,
  QrCode,
  Settings,
  Users,
  MonitorPlay,
  Trophy,
  Building2,
  Target,
  Package,
  ShoppingCart,
  ShoppingBag,
  CreditCard,
  MessageSquare,
  Plug,
  Crosshair,
  Shield,
  ListOrdered,
  Activity,
  Blocks,
  Clapperboard,
};

const getIconComponent = (iconName: string | null | undefined) => {
  if (!iconName) return Settings;
  return iconMap[iconName] || Settings;
};

const ORGANIZATION_SETTINGS_PATH = "/control/organization";

const NavItem = ({ item: navigationItem }: { item: NavigationPermission }) => {
  const visibleSubItems = navigationItem.subItems?.filter(
    (subItem) => subItem.path !== ORGANIZATION_SETTINGS_PATH,
  );
  const item = navigationItem.subItems
    ? { ...navigationItem, subItems: visibleSubItems }
    : navigationItem;
  const Icon = getIconComponent(item.icon);
  const location = useLocation();
  const pathname = location.pathname;
  const isActive =
    !!item.path &&
    (pathname === item.path || pathname.startsWith(`${item.path}/`));
  const isSubItemActive = item.subItems?.some(
    (subItem) =>
      pathname === subItem.path ||
      (!!subItem.path && pathname.startsWith(`${subItem.path}/`)),
  );
  const isHubActive =
    !!item.path &&
    (pathname === item.path || pathname.startsWith(`${item.path}/`));
  const isSectionActive = isHubActive || !!isSubItemActive;
  const [isOpen, setIsOpen] = useState(isSectionActive);
  const [prevIsSectionActive, setPrevIsSectionActive] =
    useState(isSectionActive);

  if (isSectionActive !== prevIsSectionActive) {
    setPrevIsSectionActive(isSectionActive);
    if (isSectionActive && !isOpen) {
      setIsOpen(true);
    }
  }

  if (item.subItems) {
    const subMenu = isOpen ? (
      <SidebarMenuSub className="ml-4 border-l border-sidebar-border pl-3 mt-0.5 space-y-0.5">
        {item.subItems.map((subItem) => {
          const SubIcon = getIconComponent(subItem.icon);
          const isSubActive = pathname === subItem.path;
          return (
            <SidebarMenuSubItem key={subItem.path}>
              <SidebarMenuSubButton
                asChild
                isActive={isSubActive}
                className={cn(
                  "group relative h-7 px-2 transition-colors duration-150 rounded-none",
                  "hover:bg-sidebar-accent",
                  isSubActive && "nav-item-active",
                )}
              >
                <ActiveLink
                  href={subItem.path!}
                  className="flex items-center gap-2 w-full"
                >
                  <SubIcon
                    className={cn(
                      "w-3.5 h-3.5 shrink-0",
                      isSubActive ? "text-primary" : "text-sidebar-foreground",
                    )}
                  />
                  <span
                    className={cn(
                      "text-sm",
                      isSubActive ? "text-primary" : "text-sidebar-foreground",
                    )}
                  >
                    {subItem.name}
                  </span>
                </ActiveLink>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          );
        })}
      </SidebarMenuSub>
    ) : null;

    if (item.path) {
      return (
        <SidebarMenuItem>
          <div
            className={cn(
              "group relative flex h-8 items-center rounded-none transition-colors duration-150",
              "hover:bg-sidebar-accent",
              isSectionActive && "nav-item-active",
            )}
          >
            <ActiveLink
              href={item.path}
              className="flex flex-1 items-center gap-2 px-2 min-w-0"
            >
              <Icon
                className={cn(
                  "w-4 h-4 shrink-0",
                  isSectionActive ? "text-primary" : "text-sidebar-foreground",
                )}
              />
              <span
                className={cn(
                  "text-sm truncate",
                  isSectionActive ? "text-primary" : "text-sidebar-foreground",
                )}
              >
                {item.name}
              </span>
            </ActiveLink>
            <button
              type="button"
              aria-label={isOpen ? "Colapsar submenú" : "Expandir submenú"}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsOpen(!isOpen);
              }}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-sidebar-accent/80"
            >
              <ChevronDown
                className={cn(
                  "w-3.5 h-3.5 transition-transform duration-200",
                  isSectionActive
                    ? "text-primary/70"
                    : "text-sidebar-foreground/50",
                  isOpen && "rotate-180",
                )}
              />
            </button>
          </div>
          {subMenu}
        </SidebarMenuItem>
      );
    }

    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          onClick={() => setIsOpen(!isOpen)}
          isActive={isSubItemActive}
          tooltip={item.name}
          className={cn(
            "group relative h-8 rounded-none px-2 transition-colors duration-150",
            "hover:bg-sidebar-accent",
            isSubItemActive && "nav-item-active",
          )}
        >
          <Icon
            className={cn(
              "w-4 h-4 shrink-0",
              isSubItemActive ? "text-primary" : "text-sidebar-foreground",
            )}
          />
          <span
            className={cn(
              "text-sm",
              isSubItemActive ? "text-primary" : "text-sidebar-foreground",
            )}
          >
            {item.name}
          </span>
          <ChevronDown
            className={cn(
              "ml-auto w-3.5 h-3.5 shrink-0 transition-transform duration-200",
              isSubItemActive
                ? "text-primary/70"
                : "text-sidebar-foreground/50",
              isOpen && "rotate-180",
            )}
          />
        </SidebarMenuButton>
        {subMenu}
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isActive}
        tooltip={item.name}
        className={cn(
          "group relative h-8 rounded-none px-2 transition-colors duration-150",
          "hover:bg-sidebar-accent",
          isActive && "nav-item-active",
        )}
      >
        <ActiveLink
          href={item.path!}
          className="flex items-center gap-2.5 w-full"
        >
          <Icon
            className={cn(
              "w-4 h-4 shrink-0",
              isActive ? "text-primary" : "text-sidebar-foreground",
            )}
          />
          <span
            className={cn(
              "text-sm",
              isActive ? "text-primary" : "text-sidebar-foreground",
            )}
          >
            {item.name}
          </span>
        </ActiveLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

const CategoryGroup = ({
  categoryGroup,
  currentRole,
  getEffectivePermissions,
}: {
  categoryGroup: NavigationCategoryWithItems;
  currentRole: ReturnType<
    typeof usePermissions
  >["getCurrentUserRole"] extends () => infer R
    ? R
    : never;
  getEffectivePermissions: (role: string) => NavigationPermission[];
}) => {
  const CategoryIcon = getIconComponent(categoryGroup.category.icon_name);

  const effectiveItems = currentRole
    ? getEffectivePermissions(currentRole).filter((item) =>
        categoryGroup.items.some((catItem) => catItem.path === item.path),
      )
    : [];

  if (effectiveItems.length === 0) return null;

  return (
    <SidebarGroup className="py-0">
      <SidebarGroupLabel className="px-2 py-1 mb-0.5 h-auto">
        <div className="flex items-center gap-2 w-full">
          {categoryGroup.category.icon_name && (
            <CategoryIcon className="w-3 h-3 text-primary/70 shrink-0" />
          )}
          <span className="font-display text-[10px] font-semibold text-muted-foreground uppercase tracking-widest whitespace-nowrap">
            {categoryGroup.category.name}
          </span>
          <div className="flex-1 h-px bg-sidebar-border" />
        </div>
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu className="space-y-0.5">
          {effectiveItems.map((item) => (
            <NavItem key={item.path || item.name} item={item} />
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
};

export const AppSidebar = () => {
  const { currentOrganization, isLoading: orgLoading } = useOrganization();
  const { getCurrentUserRole } = usePermissions();
  const {
    getEffectivePermissions,
    navigationConfigByCategories,
    isLoading: navLoading,
  } = useRolePermissions();
  const currentRole = getCurrentUserRole();
  const isLoading = navLoading || orgLoading;

  return (
    <Sidebar
      variant="inset"
      className="border-r border-sidebar-border bg-sidebar"
    >
      <SidebarHeader className="border-b border-sidebar-border bg-sidebar-accent/45 px-4 py-3">
        <div className="flex items-center gap-2.5">
          {currentOrganization?.logo_url ? (
            <img 
              src={currentOrganization.logo_url} 
              alt={`${currentOrganization.name} logo`} 
              width={28}
              height={28}
              loading="lazy"
              className="w-7 h-7 object-contain rounded" 
            />
          ) : (
            <img 
              src="/core-utils-logo.png" 
              alt="CoreUtils logo" 
              width={28}
              height={28}
              loading="lazy"
              className="w-7 h-7 object-contain rounded" 
            />
          )}
          <div className="flex flex-col min-w-0">
            <span className="font-display text-sm font-semibold tracking-wide text-sidebar-foreground leading-tight">
              CoreUtils
            </span>
            {currentOrganization && (
              <span className="text-[11px] text-muted-foreground truncate leading-tight">
                {currentOrganization.name}
              </span>
            )}
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2 py-3 overflow-y-auto sidebar-scrollbar">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader size="sm" text="Cargando..." />
          </div>
        ) : (
          <div className="space-y-4">
            {navigationConfigByCategories.map((categoryGroup) => (
              <CategoryGroup
                key={categoryGroup.category.id}
                categoryGroup={categoryGroup}
                currentRole={currentRole}
                getEffectivePermissions={getEffectivePermissions}
              />
            ))}
          </div>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-medium text-muted-foreground">
            CoreUtils v1.0
          </span>
          <span
            className="text-[11px] text-muted-foreground"
            suppressHydrationWarning
          >
            © {new Date().getFullYear()}
          </span>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};
